/**
 * Phase 336 wave 1 task 1.2 (#1163) — the ONE definition of consultant roster order
 * and colour assignment for the WEB, mirroring phase 334's
 * `platform/core/Teams/Cards/WeeklyReportRoster.cs` for the Teams weekly card.
 *
 * Two surfaces, one contract: the Monday Teams card and the client portal must put
 * the same consultant in the same position wearing the same hue family. Phase 334
 * pinned that contract in C#; this module is its web twin. Both are pure: rows in,
 * ordering / colour / label out. No IO, no fetch, no logging (consultant names are
 * client-confidential — the Pattern-S6 purity posture carried over).
 *
 * ── Why `loxo_user_id` ASCENDING ──────────────────────────────────────────────
 * Ask-pin decision (b) of phase 334: consultant rows sit in a FIXED roster order —
 * stable colours, no implied league table, never re-ranked by activity. The wire
 * payload's array order is a VENDOR response order (Loxo `loxo_users_list`), which
 * is not guaranteed stable week to week, so it cannot be the ordering. Ordering by
 * `loxo_user_id` ascending is stable, payload-order independent, activity
 * independent — and directly testable by shuffling the input array.
 *
 * ── Why the colour spellings are literals ─────────────────────────────────────
 * {@link CATEGORICAL_COLOR_VARS} is a LITERAL PIN, asserted spelling-by-spelling in
 * `test/consultantPalette.test.tsx` AND cross-checked against the declarations in
 * `packages/design-tokens/colors_and_type.css`. A component that invents its own
 * palette — or references a `var()` name nothing declares — is the named #839
 * failure class: hermetic tests stay green because the fixtures mirror the
 * invention, while the real surface renders a different (or transparent) fill.
 *
 * The tenth consultant onward (0-based roster index >= 9) takes
 * {@link NEUTRAL_COLOR_VAR} — the web twin of the Teams `neutral` tail. There is no
 * tenth hue: the categorical ceiling is nine (the closed Teams token set), and the
 * portal holds to it so the two surfaces cannot diverge.
 */
/**
 * The nine categorical consultant colours, as CSS `var()` references, in the fixed
 * assignment order used for roster index 0..8.
 *
 * Slot N carries the same hue FAMILY as Teams `CategoricalTokens[N]` (red, purple,
 * lavender, blue, light-blue, teal, green, lime, marigold — phase 334 task 1.1);
 * the hexes behind these tokens are tuned to the paper ground in
 * `packages/design-tokens/colors_and_type.css` and re-measured by
 * `test/fence/dataviz-tokens.fence.test.ts`. Nothing here spells a colour: this
 * module names tokens, the token file owns the values.
 */
export const CATEGORICAL_COLOR_VARS = [
    "var(--ard-cat-1)",
    "var(--ard-cat-2)",
    "var(--ard-cat-3)",
    "var(--ard-cat-4)",
    "var(--ard-cat-5)",
    "var(--ard-cat-6)",
    "var(--ard-cat-7)",
    "var(--ard-cat-8)",
    "var(--ard-cat-9)",
];
/**
 * The tail colour for roster index >= 9 — the web twin of the Teams `neutral`
 * "Others" segment. Deliberately near-achromatic: the tail is an ABSENCE of
 * identity, not a tenth identity.
 */
export const NEUTRAL_COLOR_VAR = "var(--ard-cat-neutral)";
/**
 * The family's dimmed treatment — one token, used everywhere a consultant is shown
 * but de-emphasised: support consultants (the `is_support` / `support_owner_ids`
 * rule) in the legend, in split rows and in role attribution. Exported so the
 * portal's split rows and {@link "./ChartLegend.js"} cannot drift into two
 * different greys.
 */
export const DIMMED_TEXT_VAR = "var(--ard-muted)";
/** All-digit ids compare numerically; anything else falls back to a documented total order. */
const ALL_DIGITS = /^\d+$/;
/**
 * Total order over `loxo_user_id`, ASCENDING NUMERICALLY.
 *
 * Digit strings are compared by significant length then lexicographically, which IS
 * numeric ordering and — unlike `Number(a) - Number(b)` — stays exact past
 * `Number.MAX_SAFE_INTEGER`, so a long vendor id can never collide with its
 * neighbour through float rounding. Non-numeric ids (a shape the fact store does not
 * produce today) sort AFTER every numeric id, lexicographically, so the ordering
 * stays total and deterministic rather than throwing at render time.
 */
function compareLoxoUserId(a, b) {
    const left = a.trim();
    const right = b.trim();
    const leftNumeric = ALL_DIGITS.test(left);
    const rightNumeric = ALL_DIGITS.test(right);
    if (leftNumeric && rightNumeric) {
        const l = left.replace(/^0+(?=\d)/, "");
        const r = right.replace(/^0+(?=\d)/, "");
        if (l.length !== r.length)
            return l.length - r.length;
        return l < r ? -1 : l > r ? 1 : 0;
    }
    if (leftNumeric)
        return -1;
    if (rightNumeric)
        return 1;
    return left < right ? -1 : left > right ? 1 : 0;
}
/**
 * Orders roster rows into the fixed roster order: `loxo_user_id` ASCENDING.
 *
 * Payload-order independent and activity independent (phase 334 ask-pin decision
 * (b)) — feeding the same rows in any array order returns the same sequence, which
 * is what makes each consultant's colour and row position stable week to week.
 * Ties (a duplicated id) keep their relative input order, because `Array.sort` is
 * stable in ES2019+. The input array is NOT mutated: callers hold React Query cache
 * objects, and sorting one in place would reorder every other consumer's copy.
 *
 * @param rows The roster rows in whatever order the wire supplied them. Null or
 *   empty returns an empty array.
 */
export function orderRoster(rows) {
    if (rows == null || rows.length === 0)
        return [];
    return [...rows].sort((a, b) => compareLoxoUserId(a.loxoUserId, b.loxoUserId));
}
/**
 * The colour for a consultant at 0-based ROSTER INDEX (the index in
 * {@link orderRoster}'s output — never an index into the raw payload, which would
 * repaint everyone when the vendor reorders its response).
 *
 * Index 0..8 → `var(--ard-cat-{index+1})`; index >= 9 → {@link NEUTRAL_COLOR_VAR}.
 *
 * @throws RangeError when the index is negative or not an integer — that is a caller
 *   bug (a `findIndex` miss returns -1), and silently painting a "not found"
 *   consultant with slot 1's hue would attribute one consultant's bar to another.
 */
export function colorFor(index) {
    if (!Number.isInteger(index) || index < 0) {
        throw new RangeError(`colorFor expects a non-negative integer roster index, received ${String(index)} ` +
            `(a -1 here is usually a failed lookup being painted as consultant 1)`);
    }
    return CATEGORICAL_COLOR_VARS[index] ?? NEUTRAL_COLOR_VAR;
}
/**
 * The display label for one roster row: the row's `name`, or `User {loxoUserId}`
 * when that name is null/empty/whitespace — the no-default-name invariant, so a row
 * is NEVER anonymous and a departed consultant still appearing in historical weeks
 * still reads as somebody.
 *
 * Mirrors `WeeklyReportRoster.DisplayName` (phase 334 task 1.1), which mirrors
 * `BusinessWeeklyReportCard.cs:91-93`. The name is returned VERBATIM when present —
 * trimming here would silently disagree with the Teams card's label for the same
 * consultant.
 */
export function displayName(row) {
    const name = row.name;
    if (name != null && name.trim().length > 0)
        return name;
    return `User ${row.loxoUserId}`;
}
//# sourceMappingURL=consultantPalette.js.map